import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import type { User, Session } from '@supabase/supabase-js';

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  /** true when the user has no real account (anonymous sign-in or no session) */
  isGuest: boolean;
  /** true specifically when signed in via signInAnonymously() */
  isAnonymous: boolean;
}

interface SignUpData {
  email: string;
  password: string;
  username: string;
}

interface SignInData {
  email: string;
  password: string;
}

/** Check if a user is an anonymous sign-in */
function checkIsAnonymous(user: User | null): boolean {
  return user?.is_anonymous === true;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
    isGuest: true,
    isAnonymous: false,
  });

  // Initialize: check existing session, auto-sign-in anonymously if none
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        // Existing session (could be anonymous or registered)
        if (mounted) {
          setAuthState({
            user: session.user,
            session,
            isLoading: false,
            isGuest: checkIsAnonymous(session.user),
            isAnonymous: checkIsAnonymous(session.user),
          });
        }
      } else {
        // No session — sign in anonymously so the user gets a JWT
        console.log('[Auth] No session found, signing in anonymously...');
        const { data, error } = await supabase.auth.signInAnonymously();
        if (error) {
          console.error('[Auth] Anonymous sign-in failed:', error.message);
          if (mounted) {
            setAuthState({
              user: null,
              session: null,
              isLoading: false,
              isGuest: true,
              isAnonymous: false,
            });
          }
        } else if (mounted && data.session) {
          setAuthState({
            user: data.session.user,
            session: data.session,
            isLoading: false,
            isGuest: true,
            isAnonymous: true,
          });
        }
      }
    };

    init();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (mounted) {
          const user = session?.user ?? null;
          setAuthState({
            user,
            session,
            isLoading: false,
            isGuest: !user || checkIsAnonymous(user),
            isAnonymous: checkIsAnonymous(user),
          });
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Sign up with email/password/username
  const signUp = useCallback(async ({ email, password, username }: SignUpData) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { username: username.toUpperCase() },
      },
    });

    if (error) throw error;
    return data;
  }, []);

  // Link guest player to newly created auth user
  const linkGuestPlayer = useCallback(async (guestPlayerId: string, authUserId: string) => {
    const { error } = await supabase.rpc('link_guest_to_auth_user', {
      guest_player_id: guestPlayerId,
      auth_id: authUserId,
    });
    if (error) throw error;
  }, []);

  // Sign in with email/password
  const signIn = useCallback(async ({ email, password }: SignInData) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    // Clear stale player ID so the next session (anonymous) gets a fresh one
    localStorage.removeItem('dicepit_playerId');
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  // Send password reset email
  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset`,
    });
    if (error) throw error;
  }, []);

  // Update password (for reset flow)
  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) throw error;
  }, []);

  // Fetch the authenticated user's player row from DB
  const fetchPlayerProfile = useCallback(async () => {
    if (!authState.user) return null;

    const { data, error } = await supabase
      .from('players')
      .select('id, name, level, exp, wins, losses, draws')
      .eq('auth_user_id', authState.user.id)
      .single();

    if (error) return null;
    return data;
  }, [authState.user]);

  return {
    user: authState.user,
    session: authState.session,
    isLoading: authState.isLoading,
    isGuest: authState.isGuest,
    isAnonymous: authState.isAnonymous,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    linkGuestPlayer,
    fetchPlayerProfile,
  };
};
